import React, { useState, useRef, useEffect, useContext } from 'react';
import propTypes from 'prop-types';
import md5 from 'crypto-js/md5';
import cssStyles from 'styles/form_group.module.scss';
import { Button, Switch, Icon } from 'components';
import PageScrollContext from 'context/PageScrollContext';
import classNames from 'lib/classNames';

const FormGroup = ({
    label,
    children,
    className = '',
    onFocus,
    onBlur,
    info,
    minWidth = 410,
    focusCondition = () => false,
    styles = { content: {}, title: {} },
    noBorderBottom,
    collapsable = false,
    isOpen = true,
    fullWidth = false,
    withSwitch = false,
    beta = false
}) => {
    const group = useRef(null);
    const groupContent = useRef(null);
    const { scrollTop, getScrollTop } = useContext(PageScrollContext) || {};
    const [collapsed, setCollapsed] = useState(isOpen);
    const [maxHeight, setMaxHeight] = useState(0);

    const handleClick = container => event => {
        const focusWithIn =
            container === document.activeElement ||
            container.contains(document.activeElement) ||
            focusCondition({
                activeElement: document.activeElement,
                event,
                container
            }) ||
            !![...container.querySelectorAll('.sketch-picker')].find(picker =>
                picker.contains(event.target)
            );
        if (focusWithIn && onFocus) onFocus();
        else if (!focusWithIn && onBlur) onBlur();
    };

    useEffect(
        () => {
            if (onFocus || onBlur) {
                document.addEventListener('mouseup', handleClick(group.current));
            }
            return () => {
                if (onFocus || onBlur) {
                    document.removeEventListener('mouseup', handleClick(group.current));
                }
            };
        },
        [group.current, onFocus, onBlur]
    );

    useEffect(
        () => {
            if (groupContent.current) {
                setMaxHeight(groupContent.current.offsetHeight);
            }
        },
        [collapsed]
    );

    return (
      <div
        className={classNames(
          `flex flex-wrap sm:flex-col md:flex-col lg:flex-row border-gray-300 border-b ${
            collapsable ? "" : "pt-6 pb-6"
          } group-wrapper`
        )}
      >
        <div
          className={classNames(
            `${
              collapsable
                ? "w-full flex-grow pt-6 pb-6"
                : `${
                    fullWidth
                      ? "w-full"
                      : "flex-grow sm:w-full md:w-full lg:w-1/3"
                  } pb-3`
            } group-left-side flex flex-row pr-2 justify-between items-start cursor-pointer`
          )}
          style={{ ...styles.title, transition: "margin 0.4s ease" }}
          onClick={() => {
            setCollapsed(!collapsed);
          }}
        >
          <div className={classNames("flex-col")}>
            <label
              className={classNames(
                `${
                  collapsable
                    ? "text-lg text-gray-700"
                    : "text-base text-gray-800"
                } mb-2 font-bold capitalize relative group-label cursor-pointer`
              )}
              onClick={(e) => {
                if (!scrollTop) return;
                const padding =
                  1.5 *
                  parseFloat(
                    getComputedStyle(document.documentElement).fontSize
                  );
                scrollTop(e.target.closest(".mu-group-wrapper").offsetTop - 0);
              }}
              id={`id_${md5(label)}`}
            >
              <Icon
                icon="hashtag"
                style={{
                  maxHeight: 27,
                }}
                className="absolute top-0 left-0 transform -translate-x-full text-gray-500 h-full pr-1 group-label-icon hidden"
              />
              {label}
            </label>
            {!!info && (
              <p
                className={classNames("text-gray-500 text-xs italic")}
                dangerouslySetInnerHTML={{
                  __html: info.replace("\n", "<br/>"),
                }}
              />
            )}
          </div>

          <div className={classNames("flex flex-wrap")}>
            {beta && (
              <span
                className={classNames(
                  "flex-center bg-orange-500 tracking-wider py-1 px-2 rounded-lg text-white text-xxs font-bold",
                  {
                    "mr-2": withSwitch,
                  }
                )}
                style={{ top: -2, right: -2 }}
              >
                Beta
              </span>
            )}
            {!!withSwitch && !collapsable && (
              <Switch BindToContext={withSwitch} />
            )}
          </div>
          {collapsable && (
            <div className={classNames("pt-2")}>
              <svg
                className={classNames("w-4 h-3")}
                viewBox="0 0 14 8"
                fill="none"
                style={{
                  transform: collapsed ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.4s ease",
                }}
              >
                <path
                  d="M1 1L7 7L13 1"
                  stroke="#718096"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          )}
        </div>

        <div
          className={classNames(
            `relative sm:w-full md:w-full ${
              collapsable
                ? "lg:w-2/3 ml-auto sm:flex-grow xl:flex-grow-0"
                : `${
                    fullWidth ? "" : "lg:w-2/3 "
                  }flex-grow flex-shrink-0 min-w-2/3 overflow-visible`
            } pl-2 sm:pl-0 md:pl-0`
          )}
          ref={group}
          style={{
            ...styles.content,
            minWidth,
            ...(collapsable
              ? {
                  maxHeight: collapsed ? "0px" : `${maxHeight}px`,
                  opacity: collapsed ? 0 : 1,
                  overflow: collapsed ? "hidden" : "visible",
                  transition: "all 0.6s ease",
                }
              : {}),
          }}
        >
          <div
            ref={groupContent}
            className={classNames("w-full px-1 pt-1", { "pb-6": collapsable })}
          >
            {children}
          </div>
        </div>
      </div>
    );
};

FormGroup.propTypes = {
    // children: propTypes.Children.isRequired,
    label: propTypes.string
};
export default FormGroup;
